import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCcw, CheckCircle2, XCircle, Clock, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StatusBadge = ({ result }) => {
  if (result === 'ok' || (typeof result === 'string' && result.startsWith('ok'))) {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] uppercase tracking-wider gap-1">
        <CheckCircle2 className="w-3 h-3" /> {result}
      </Badge>
    );
  }
  if (result === 'fail' || (typeof result === 'string' && result.startsWith('fail'))) {
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] uppercase tracking-wider gap-1">
        <XCircle className="w-3 h-3" /> {result}
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] uppercase tracking-wider gap-1">
      <Clock className="w-3 h-3" /> {result || '—'}
    </Badge>
  );
};

const CronHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Cache-buster query param defeats Service Worker / CDN caching.
      // NOTE: No custom headers — they trigger CORS preflight which can fail on production.
      const cb = Date.now();
      const res = await fetch(`${API_URL}/api/admin/cron-health?_=${cb}`, {
        cache: 'no-store',
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} — ${body.slice(0, 200)}`);
      }
      const j = await res.json();
      if (!j || !j.tasks) {
        throw new Error('Backend returned empty payload — likely older deployment without cron-health endpoint.');
      }
      setData(j);
    } catch (e) {
      setError(`${e.message || e}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="cron-health-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🔄 Cron Health</h2>
          <p className="text-sm text-gray-600 mt-1">
            Live status of every background scheduler. If a task hasn't fired when expected, you'll see it here first.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm" data-testid="cron-refresh-btn">
          <RefreshCcw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* ERROR STATE — shown when backend endpoint isn't reachable */}
      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Could not load cron health</h3>
              <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
              <p className="text-xs text-red-700 mt-2">
                <strong>Fix:</strong> Hit "Save to GitHub" then redeploy from the Emergent dashboard. After redeploy, this page will populate.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !error && (
        <p className="text-sm text-gray-500" data-testid="cron-loading">Loading…</p>
      )}

      {data && (
        <p className="text-xs text-gray-500" data-testid="cron-clock">
          Server clock (IST): <span className="font-mono text-gray-900">{data.ist_now}</span>
        </p>
      )}

      {/* Env var check */}
      {data?.env_check && (
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">🔐 Required Env Vars</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.env_check).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs">
                  {v ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  )}
                  <span className={`font-mono ${v ? 'text-gray-700' : 'text-red-600 font-semibold'}`}>{k}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks list */}
      {data?.tasks && data.tasks.length > 0 && (
        <div className="space-y-2" data-testid="cron-tasks-list">
          {data.tasks.map((t, idx) => (
            <Card key={`${t.name}_${idx}`} className={`border-l-4 ${t.fired_today ? 'border-l-green-500' : 'border-l-amber-500'}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{t.name}</span>
                    <StatusBadge result={t.last_result} />
                    {t.fired_today && (
                      <Badge className="bg-green-50 text-green-700 border-green-200 text-[10px]">
                        ✓ Fired today
                      </Badge>
                    )}
                    {!t.fired_today && (
                      <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                        Not yet today
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t.schedule}</p>
                  {/* Show detailed error info for failed tasks (e.g., Fyers TOTP) */}
                  {t.extra && (t.extra.last_run_step || t.extra.last_run_detail) && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-[11px] font-mono text-red-900 overflow-x-auto">
                      {t.extra.last_run_step && <div><strong>step:</strong> {t.extra.last_run_step}</div>}
                      {t.extra.last_run_detail && <div className="whitespace-pre-wrap break-all"><strong>detail:</strong> {t.extra.last_run_detail}</div>}
                    </div>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500 min-w-[180px]">
                  <div className="font-mono text-gray-700">{t.last_fire || '—'}</div>
                  {t.last_fire_age && <div className="text-amber-700">{t.last_fire_age}</div>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CronHealthTab;
